import { updateProject } from "@/components/common/project-provider/update-project";
import { AppThunk } from "@/store/store";
import { IElementBase } from "@faro-lotv/ielement-types";
import { ProjectApi, ResponseSize } from "@faro-lotv/service-wires";
import { chunk } from "es-toolkit";

/** The maximum number of iElements that can be requested in one query. */
const MAX_IELEMENTS_PER_REQUESTS = 20;

/** The maximum number of requests that can be done in parallel to the Project API */
const MAX_PARALLEL_REQUESTS = 20;

/**
 *
 * Split a request to load a set of iElements from the Project API in chunks
 * to create shorter URLs
 *
 * @param projectApiClient The ProjectAPI client
 * @param iElements The list of iElements to load
 * @returns The app thunk that, once dispatched, updates the project state
 */
export function loadChunkedIElements(
  projectApiClient: ProjectApi,
  iElements: IElementBase[],
): AppThunk<Promise<void>> {
  return async (dispatch): Promise<void> => {
    // Split the iElements in chunks to create shorter URLs requests
    // and to avoid failures from the Project API
    const iElementsChunks = chunk(iElements, MAX_IELEMENTS_PER_REQUESTS);

    // N iElements / MAX_IELEMENTS_PER_REQUESTS => M requests (i.e. iElementsChunks)
    // M requests / MAX_PARALLEL_REQUESTS => P sessions with (at most) MAX_PARALLEL_REQUESTS parallel requests (i.e. requestSessions)
    const requestsSessions = chunk(iElementsChunks, MAX_PARALLEL_REQUESTS);
    for (const session of requestsSessions) {
      const requests: Array<Promise<void>> = [];
      for (const chunk of session) {
        requests.push(
          dispatch(
            updateProject({
              projectApi: projectApiClient,
              iElementQuery: {
                ancestorIds: chunk.map((e) => e.id),
                expectedResponseSize: ResponseSize.small,
              },
            }),
          ),
        );
      }
      await Promise.all(requests);
    }
    return Promise.resolve();
  };
}
